import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { formatDate } from "../../util/helper";

export const navigateToDownloadDetails = async (
  calculationId,
  dispatch,
  setIsGeneratingPDF
) => {
  try {
    dispatch(fetchStart());
    setIsGeneratingPDF(true);

    const response = await axiosInstance.get(
      `${BASE_API_CONFIG.baseURL}/pricecalculation/${calculationId}`
    );
    const data = response.data;

    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Page dimensions
    const pageWidth = 595;
    const pageHeight = 842;
    const margin = 50;
    const bottomMargin = 50;

    // Table configuration
    const columnTitles = [
      { text: "Description", width: 120 },
      { text: "Rate Per Unit", width: 100 },
      { text: "Quantity", width: 80 },
      { text: "GST (%)", width: 50 },
      { text: "GST Amount(INR)", width: 90 },
      { text: "Amount(INR)", width: 90 },
    ];

    const headerHeight = 25;
    const cellPadding = 5;
    const lineHeight = 12;

    // Function to wrap text
    function wrapText(text, font, fontSize, maxWidth) {
      if (!text) return [""];

      const words = text.split(" ");
      let lines = [];
      let currentLine = "";

      for (const word of words) {
        const testLine = currentLine ? currentLine + " " + word : word;
        const testWidth = font.widthOfTextAtSize(testLine, fontSize);

        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }

      if (currentLine) lines.push(currentLine);
      return lines;
    }

    // Function to add a new page
    const addNewPage = () => {
      const newPage = pdfDoc.addPage([pageWidth, pageHeight]);
      let currentY = pageHeight - margin;

      // Add title and header on new page
      newPage.drawText("Price Details", {
        x: margin,
        y: currentY,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      currentY -= 25;

      newPage.drawText(`${data.templateName}`, {
        x: margin,
        y: currentY,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });

      newPage.drawText(`Date: ${formatDate(data.createdAt)}`, {
        x: 400,
        y: currentY,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });
      currentY -= 15;

      newPage.drawLine({
        start: { x: margin, y: currentY },
        end: { x: pageWidth - margin, y: currentY },
        thickness: 1,
        color: rgb(0, 0, 0),
      });
      currentY -= 15;

      return { page: newPage, currentY };
    };

    // Function to draw table header
    const drawTableHeader = (page, yPos) => {
      page.drawRectangle({
        x: margin,
        y: yPos - headerHeight,
        width: columnTitles.reduce((sum, col) => sum + col.width, 0),
        height: headerHeight,
        color: rgb(0.2, 0.4, 0.8),
      });

      let currentX = margin;
      columnTitles.forEach((column) => {
        const textWidth = boldFont.widthOfTextAtSize(column.text, 10);
        const centeredX = currentX + (column.width - textWidth) / 2;

        page.drawText(column.text, {
          x: centeredX,
          y: yPos - headerHeight / 2 - 4,
          size: 10,
          font: boldFont,
          color: rgb(1, 1, 1),
        });

        currentX += column.width;
      });

      return yPos - headerHeight - 1;
    };

    // Function to draw cell content
    const drawCellContent = (page, currentY, rowValues, columnTitles, currentXStart) => {
      let currentX = currentXStart;
      for (let colIndex = 0; colIndex < columnTitles.length; colIndex++) {
        const column = columnTitles[colIndex];
        let textX = currentX + cellPadding;
        const wrappedLines = wrapText(
          rowValues[colIndex],
          font,
          9,
          column.width - cellPadding * 2
        );

        // Right-align for numeric columns (GST Amount and Amount)
        if (colIndex === 4 || colIndex === 5) {
          const textWidth = font.widthOfTextAtSize(wrappedLines[0], 9);
          textX = currentX + column.width - textWidth - cellPadding;
        }

        // Draw each line of text
        for (let lineIndex = 0; lineIndex < wrappedLines.length; lineIndex++) {
          const line = wrappedLines[lineIndex];
          page.drawText(line, {
            x: textX,
            y: currentY - cellPadding - lineHeight - lineIndex * lineHeight,
            size: 9,
            font,
            color: rgb(0, 0, 0),
          });
        }

        currentX += column.width;
      }
    };

    // Start with first page
    let { page, currentY } = addNewPage();

    // Draw header on first page
    currentY = drawTableHeader(page, currentY);

    // Process items
    if (!data.items || data.items.length === 0) {
      page.drawText("No items to display", {
        x: margin,
        y: currentY,
        size: 12,
        font,
        color: rgb(0, 0, 0),
      });
    } else {
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i];

        // Check if we need a new page
        if (currentY < bottomMargin + 100) {
          // Reserve space for at least one row
          const newPageInfo = addNewPage();
          page = newPageInfo.page;
          currentY = drawTableHeader(newPageInfo.page, newPageInfo.currentY);
        }

        const rowValues = [
          item.description || "",
          `${item.currency} ${
            item.ratePerUnit
              ? Number(item.ratePerUnit).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "0.00"
          } / ${item.uom}`,
          `${
            item.noOfUnits
              ? Number(item.noOfUnits).toLocaleString("en-IN")
              : "0"
          } ${item.uom}`,
          item.gstRate ? `${item.gstRate}%` : "0%",
          item.gstAmount
            ? Number(item.gstAmount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00",
          item.amount
            ? Number(item.amount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00",
        ];

        // Calculate max lines needed for this row
        const maxLines = Math.max(
          ...rowValues.map(
            (val, idx) =>
              wrapText(val, font, 9, columnTitles[idx].width - cellPadding * 2)
                .length
          )
        );

        const rowHeightForThisRow = maxLines * lineHeight + cellPadding * 2;
        const gapBetweenRows = 0;

        // Draw row background for even rows
        if (i % 2 === 0) {
          page.drawRectangle({
            x: margin,
            y: currentY - rowHeightForThisRow,
            width: columnTitles.reduce((sum, col) => sum + col.width, 0),
            height: rowHeightForThisRow,
            color: rgb(0.95, 0.95, 0.95),
          });
        }

        // Draw cell borders
        page.drawRectangle({
          x: margin,
          y: currentY - rowHeightForThisRow,
          width: columnTitles.reduce((sum, col) => sum + col.width, 0),
          height: rowHeightForThisRow,
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 1,
        });

        // Draw cell content
        drawCellContent(page, currentY, rowValues, columnTitles, margin);

        // Move down for next row
        currentY -= rowHeightForThisRow + gapBetweenRows;
      }
    }

    // Calculate summary values
    const getPrimaryItemQuantity = () => {
      const primaryItem = data.items.find((item) => item.isPrimary === true);
      return primaryItem ? Number(primaryItem.noOfUnits) : 0;
    };
    const noOfUnits = getPrimaryItemQuantity();
    const foreignCurrencyItem = data.items?.find(
      (item) => item.currency !== "INR"
    );
    const indianCurrency = "INR";
    const foreignCurrency = foreignCurrencyItem?.currency;
    const exchangeRate = foreignCurrencyItem
      ? parseFloat(foreignCurrencyItem.exchangeRate)
      : 1;

    const totalAmountINR = Number(data.totalAmount || 0);
    const totalAmountForeign = foreignCurrency
      ? totalAmountINR / exchangeRate
      : 0;

    const totalPricePerUnitINR = noOfUnits > 0 ? totalAmountINR / noOfUnits : 0;
    const totalPricePerUnitOther =
      noOfUnits > 0 ? totalAmountForeign / noOfUnits : 0;

    // Check if we need a new page for the summary
    const summaryHeight = 15 * 4 + 20; // 4 lines with 15px spacing + 20px top margin
    if (currentY < bottomMargin + summaryHeight) {
      const newPageInfo = addNewPage();
      page = newPageInfo.page;
      currentY = newPageInfo.currentY;
    }

    // Position summary below the table with some spacing
    currentY -= 20;

    const summaryLines = [
      `Total Amount (${indianCurrency}): ${totalAmountINR.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}`,
      `Total Price Per Unit (${indianCurrency}): ${totalPricePerUnitINR.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}`,
    ];

    if (foreignCurrency) {
      summaryLines.push(
        `Total Amount (${foreignCurrency}): ${totalAmountForeign.toLocaleString(
          "en-IN",
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )}`,
        `Total Price Per Unit (${foreignCurrency}): ${totalPricePerUnitOther.toLocaleString(
          "en-IN",
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )}`
      );
    }

    const maxWidth = columnTitles.reduce((sum, col) => sum + col.width, 0);
    
    summaryLines.forEach((line, index) => {
      const textWidth = boldFont.widthOfTextAtSize(line, 10);
      page.drawText(line, {
        x: margin + maxWidth - textWidth,
        y: currentY - index * 15,
        size: 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
    });

    // Save & Download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Quotation-${data.templateName}-${formatDate(
      data.createdAt
    )}.pdf`;
    link.click();

    dispatch(fetchSuccess());
  } catch (error) {
    console.error("PDF generation failed:", error);
    dispatch(fetchSuccess());
  } finally {
    setIsGeneratingPDF(false);
  }
};